import React, {
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { SbEditableContent } from 'storyblok-react';
import StoryblokClient, { ISbStoryData } from 'storyblok-js-client';
import { Props } from '../types';
import { PageStateContext, StoryblokService } from '../../services';
import { Teaser, TeaserFields } from './teaser';
import { getTeaserMapping, TeaserPageTypes } from '../../services/compMapping/teasers';

type PageProps = Props<{ body: SbEditableContent[] }>;

const teaserMappings = (article, teaserSettings, globalSettings): TeaserFields | null => {
  if (!article) return null;
  return getTeaserMapping(TeaserPageTypes.PRESS)(article, teaserSettings, globalSettings);
};

export const PagePressOverview = ({ blok, getComponent }: PageProps): JSX.Element => {
  const { setPictureCreditsAdditionalData, settings: pageSettings } = useContext(PageStateContext);

  // eslint-disable-next-line max-len
  const convertStoriesToTeasers = (items) => items.map((teaser) => teaserMappings(teaser, blok, pageSettings));

  const totalStories = useRef(0);
  const itemsToLoad: number = parseInt(pageSettings?.items_to_load, 10) || 24;
  const [teasers, setTeasers] = useState([]);
  const teaserComponents = useRef([]);
  const storyblokJsClient = useRef(null);
  const currentPage = useRef(1);
  const moreButton = useRef(null);
  const isMoreButtonVisible = useRef(false);

  const fetchTeasers = async (): Promise<{ stories: ISbStoryData[]; total: number }> => {
    const slugWithLang = '';

    const filter = {
      per_page: itemsToLoad,
      sort_by: 'content.release_date:desc,content.boost_to_top:asc',
      page: currentPage.current,
      filter_query: {
        component: {
          in: 'page-press-news',
        },
      },
    };

    const { data, total } = await storyblokJsClient.current?.getStory(slugWithLang, filter);

    return {
      stories: data.stories,
      total,
    };
  };

  const onMoreButtonClick = (): void => {
    fetchTeasers().then(({ stories, total }) => {
      setTeasers((prev) => [...prev, ...stories]);
      totalStories.current = total;
      currentPage.current += 1;
    });
  };

  useEffect(() => {
    storyblokJsClient.current = new StoryblokClient({
      accessToken: StoryblokService.getConfig().options.accessToken as string,
    });
    // eslint-disable-next-line no-unused-expressions
    moreButton.current?.addEventListener('rocheButtonClick', onMoreButtonClick);
    // load 1st page. Note: I removed static query as here we are using variable "per_page"
    // which is in conflict with static query, where variables are not allowed
    onMoreButtonClick();
  }, []);

  useEffect(() => {
    isMoreButtonVisible.current = teasers?.length < totalStories.current;
    // eslint-disable-next-line max-len
    teaserComponents.current = convertStoriesToTeasers(teasers);
    setPictureCreditsAdditionalData([...teaserComponents.current]);
  }, [teasers]);

  return (
    <>
      <roche-container>
        {blok.headline && <roche-headline level={1} headline={blok.headline} />}
        <roche-columns center-children={'false'} multiple-rows={true}>
          {teaserComponents?.current?.map((teaser) => (
            /* Setting the key to random UUID to force react rerendering. Conflict with stencil */
            <roche-column key={crypto.randomUUID()} span={4}>
              <Teaser blok={teaser} getComponent={getComponent} />
            </roche-column>
          ))}
        </roche-columns>
      </roche-container>

      <roche-grid layout="12" style={{ display: isMoreButtonVisible.current ? 'block' : 'none' }}>
        <roche-button ref={moreButton} text={pageSettings?.load_more_button_label} />
      </roche-grid>
    </>
  );
};
